"use client";

import { NavArrowDown } from "iconoir-react";
import { useId } from "react";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface MultiSelectFilterProps {
  label: string;
  options: string[];
  selected: string[];
  placeholder: string;
  /** Template containing `{count}`, shown when more than one option is selected. */
  selectedCountLabel: string;
  onToggle: (value: string, checked: boolean) => void;
}

/** Figma 12400:39520 — labelled "Select options" dropdown with checkbox items. */
export function MultiSelectFilter({
  label,
  options,
  selected,
  placeholder,
  selectedCountLabel,
  onToggle,
}: MultiSelectFilterProps) {
  const labelId = useId();
  const valueId = useId();

  const summary =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? selected[0]
        : selectedCountLabel.replace("{count}", String(selected.length));

  return (
    <div className="flex flex-col gap-2">
      <span id={labelId} className="text-ds-body-xs text-grey-950">
        {label}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-labelledby={`${labelId} ${valueId}`}
          disabled={options.length === 0}
          className="border-grey-100 bg-grey-0 rounded-ds-m hover:border-grey-200 focus-visible:ring-pulse-400 flex h-10 w-full items-center justify-between gap-2 border px-4 transition-colors duration-[180ms] focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50"
        >
          <span
            id={valueId}
            className={cn(
              "text-ds-body-xs truncate",
              selected.length === 0 ? "text-grey-600" : "text-grey-950"
            )}
          >
            {summary}
          </span>
          <NavArrowDown className="text-grey-600 size-4 shrink-0" aria-hidden />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="max-h-72 overflow-y-auto">
          {options.map((option) => (
            <DropdownMenuCheckboxItem
              key={option}
              checked={selected.includes(option)}
              onCheckedChange={(checked) => onToggle(option, checked)}
              className="text-ds-body-sm"
            >
              {option}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
